import React, { useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import useIntersection from '@/hooks/use-intersection/index';
import useDebounce from '@/hooks/use-debounce/index';

const LazyLoadWrapper = ({ loadMore, hasMore, isLoading, triggerIndex = 1, children }) => {
    const containerRef = useRef(null);
    const loadTriggerRef = useRef(null);
    const [setIntersectionTarget, isTriggerVisible, setRoot] = useIntersection({ threshold: 1.0 });

    const triggerDebounce = useDebounce(() => {
        if (isTriggerVisible && hasMore && !isLoading) {
            loadMore();
        }
    }, 'infiniteScroll');

    useEffect(() => {
        setRoot(containerRef.current);
    }, [setRoot]);

    useEffect(() => {
        if (Array.isArray(children.props.items) && children.props.items.length > triggerIndex) {
            setIntersectionTarget(loadTriggerRef.current);
        }
    }, [children.props.items, triggerIndex, setIntersectionTarget]);

    useEffect(() => {
        triggerDebounce();
    }, [isTriggerVisible, hasMore, loadMore, isLoading, triggerDebounce]);

    return (
        <>
            {React.cloneElement(children, {
                ref: containerRef,
                loadTriggerRef,
                triggerIndex
            })}
        </>
    );
};

LazyLoadWrapper.propTypes = {
    loadMore: PropTypes.func.isRequired,
    hasMore: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    triggerIndex: PropTypes.number,
    children: PropTypes.element.isRequired
};

export default memo(LazyLoadWrapper);
